import { AxiosResponse } from 'axios';

import { useQueryClient } from '@tanstack/react-query';

import { PAY_STATEMENT_TAB_CODE } from '../../constants';
import { TypeCodeState } from '../../types';
import { axiosInstance } from '~/services/fetchServiceV2';
import { TypeErrorFormat, requestHelper } from '~/utils/fetchHelper';

type TypePayStatementDetailParams = {
  payslipType: string;
  paymentAt: string;
};

type TypePayStatementDetailResponse<T> = {
  code: string;
  message: string;
  data: T;
};

export type TypePayStatementDetailCommon = {
  /** 이름 */
  name: string;
  /** 생년월일 */
  birthday: string;
  /** 연락처 */
  phoneNo: string;
  /** 기준시급 / 계약시급 */
  hourPay: number;
  /** 급여지급일 */
  paymentAt: string;
  /** 연휴수당 */
  holidayPay?: number;
  /** 연휴 근무 시간 (분) */
  holidayTime?: number;
  /** 연휴 근무 횟수 */
  holidayCount?: number;
  /** 프로모션 수당 */
  promotionPay?: number;
  /** 프로모션 근무 시간 (분) */
  promotionTime?: number;
  /** 프로모션 근무 횟수 */
  promotionCount?: number;
  /** 연장수당 */
  overWorkPay?: number;
  /** 연장 근무 시간 (분) */
  overWorkTime?: number;
  /** 연장 근무 횟수 */
  overTimeCount?: number;
  /** 심야수당 */
  nightPay?: number;
  /** 심야근무 시간 (분) */
  nightTime?: number;
  /** 심야 근무 횟수 */
  nightCount?: number;
  /** 연차수당 */
  annualPay?: number;
  /** 연차수당 시간 (분) */
  annualTime?: number;
  /** 연차 횟수 */
  annualCount?: number;
  /** 특근일자 */
  specialWorkDay?: string;
  /** 특근 금액 */
  specialWorkPay?: number;
  /** 특근 시간 (분) */
  specialWorkTime?: number;
  /** 기타수당 */
  etcPay?: number;
};

export type TypePayStatementDetailByPerDiem = TypePayStatementDetailCommon & {
  /** 믈류센터명 */
  centerName?: string;
  /** 근무 일시 */
  workDate: string;
  /** 근무시간 shift */
  workTimeShift: string;
  /** 계약일급 */
  contractDayPay: number;
  /** 계약시간 */
  contractDayTime: string;
  /** 총 급여 */
  totalPay: number;
  /** 공제항목 > 고용보험 */
  g004: number;
  /** 공제항목 > 4대보험 */
  g007: number;
  /** 공제항목 > 4대보험 미수금 */
  g007UncollectedAmount: number;
  /** 공제항목 > 소득세 */
  g005: number;
  /** 공제항목 > 지방소득세 */
  etcG005: number;
  /** 공제항목 > 총 공제액 */
  deduction: number;
  /** 공제항목 > 실 지급액 */
  net: number;
};

export type TypePayStatementDetailByEtc = TypePayStatementDetailCommon & {
  /** 반영기간 (시작일 ~ 종료일) */
  period: string;
  /** 총 근무일수 */
  totalWorkDay: number;
  /** 총 근무시간 */
  totalWorkTimes: number;
  /** 급여명세 유형 */
  paySlipType: TypeCodeState;
  /** 등록일시 */
  registeredAt: string;
  /** 총 급여 */
  totalPay: number;
  /** 공제항목 > 고용보험 */
  g004: number;
  /** 공제항목 > 4대보험 */
  g007: number;
  /** 공제항목 > 4대보험 미수금 */
  g007UncollectedAmount: number;
  /** 공제항목 > 소득세 */
  g005: number;
  /** 공제항목 > 지방소득세 */
  etcG005: number;
  /** 공제항목 > 총 공제액 */
  deduction: number;
  /** 공제항목 > 실 지급액 */
  net: number;
};

/** 일급, 기타 급여명세서 상세 조회 */
export const useGetPayStatementDetail = () => {
  const queryClient = useQueryClient();

  const requestGetPayStatementDetail = async ({
    params,
    onSuccess,
    onError,
  }: {
    params: TypePayStatementDetailParams;
    onSuccess?: (
      response: TypePayStatementDetailResponse<TypePayStatementDetailByPerDiem | TypePayStatementDetailByEtc>
    ) => void;
    onError?: (error: TypeErrorFormat) => void;
  }) => {
    const endpoint = '/labor/payslips';

    const perDiemParams = `per-diem/${params.paymentAt}`;
    const ectParams = `etc/${params.paymentAt}?payslipType=${params.payslipType}`;

    const query = queryClient.ensureQueryData<
      AxiosResponse<TypePayStatementDetailResponse<TypePayStatementDetailByPerDiem | TypePayStatementDetailByEtc>>
    >({
      queryKey: ['payStatementsDetail', params.payslipType, params.paymentAt],
      queryFn: () =>
        axiosInstance.get(
          `${endpoint}/${params.payslipType === PAY_STATEMENT_TAB_CODE.PER_DIEM ? perDiemParams : ectParams}`
        ),
      gcTime: 0,
    });

    return requestHelper(() => query, onSuccess, onError);
  };

  return requestGetPayStatementDetail;
};
